// Keyboard + mouse capture, reduced to game intents: a world-space movement
// vector, a facing yaw, and camera orbit parameters. DOM-only; the renderer
// consumes the numbers.

import { wrapAngle } from "@bebop/shared";

export interface OrbitState {
  /** Angle of the camera around the avatar (radians). 0 = camera south of avatar. */
  yaw: number;
  /** Elevation angle (radians), clamped. */
  pitch: number;
  /** Distance from the avatar (metres). */
  distance: number;
}

export interface MoveIntent {
  moveX: number;
  moveZ: number;
  /** Facing: direction of travel while moving, previous facing when idle. */
  yaw: number;
  moving: boolean;
  /** Shift is held (surface sprint; boost at a ship's controls). */
  sprint: boolean;
  /** Walk toggle is on (X key). */
  walk: boolean;
  /** Vertical intent while flying: Space climbs, C sinks. −1..1. */
  climb: number;
  /** Boost held (same Shift as sprint; ships read this one). */
  boost: boolean;
}

const PITCH_MIN = 0.08;
const PITCH_MAX = 1.15;
/** At a ship's controls the camera may drop below the horizon — the coil
 *  gun aims along the camera, and a gun that could never point up would
 *  make every level or higher target unhittable. */
const PITCH_MIN_FLIGHT = -0.55;
const DISTANCE_MIN = 2.5;
const DISTANCE_MAX = 9;
const LOOK_SENSITIVITY = 0.0055;

export class InputController {
  readonly orbit: OrbitState = { yaw: 0, pitch: 0.42, distance: 5 };

  /** While ANY text-taking UI is focused — the chat box, a board panel's
   * field — movement keys are ignored. Promoted from chatFocused when the
   * first panel with a text input arrived (trades.md item 5): without it
   * typing a lot size would walk your avatar and steal your Enter key. */
  uiFocused = false;
  /** Flying: widens the pitch range downward (game.ts sets this). */
  flightCamera = false;

  /** Walk-gait toggle (X). Persists until toggled again; sprint overrides. */
  private walkToggled = false;
  /** Set on E keydown; consumed once per frame by the game loop. */
  private interactQueued = false;
  /** Routing keypress (1/2/3), consumed once per frame while flying. */
  private routeQueued: "burners" | "coils" | "veil" | undefined;
  /** Combat keypress (F strike / V evade / T steady), consumed per frame. */
  private combatQueued: "strike" | "evade" | "steady" | undefined;

  private pressed = new Set<string>();
  private dragging = false;
  private lastYaw = Math.PI; // matches SPAWN.yaw until seeded from the server

  /** Adopt the server-restored facing so idle intents don't overwrite it. */
  seedYaw(yaw: number): void {
    this.lastYaw = yaw;
  }

  /** Whether the walk-gait toggle is on (HUD shows it). */
  get walking(): boolean {
    return this.walkToggled;
  }

  /** The trigger is held (F). The game loop meters the actual shots. */
  get firing(): boolean {
    return this.has("KeyF");
  }

  /** True once per E press; clears on read. */
  consumeInteract(): boolean {
    const queued = this.interactQueued;
    this.interactQueued = false;
    return queued;
  }

  /** The routing key pressed this frame, if any; clears on read. */
  consumeRoute(): "burners" | "coils" | "veil" | undefined {
    const queued = this.routeQueued;
    this.routeQueued = undefined;
    return queued;
  }

  /** The combat key pressed this frame, if any; clears on read. */
  consumeCombat(): "strike" | "evade" | "steady" | undefined {
    const queued = this.combatQueued;
    this.combatQueued = undefined;
    return queued;
  }

  /** Whether the brace key (G) is held right now. */
  get bracing(): boolean {
    return this.has("KeyG");
  }

  attach(canvas: HTMLElement): void {
    window.addEventListener("keydown", (e) => {
      if (this.uiFocused || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.code === "KeyX" && !e.repeat) this.walkToggled = !this.walkToggled;
      if (e.code === "KeyE" && !e.repeat) this.interactQueued = true;
      // The defensive verbs (surface, on foot — the game loop gates that).
      if (!e.repeat) {
        if (e.code === "KeyF") this.combatQueued = "strike";
        if (e.code === "KeyV") this.combatQueued = "evade";
        if (e.code === "KeyT") this.combatQueued = "steady";
      }
      // The routing family: one key per manifold. Pressing the manifold
      // you already favor vents back to even (game.ts decides that; this
      // layer just reports which key).
      if (!e.repeat) {
        if (e.code === "Digit1") this.routeQueued = "burners";
        if (e.code === "Digit2") this.routeQueued = "coils";
        if (e.code === "Digit3") this.routeQueued = "veil";
      }
      // Space climbs; keep it away from the page (scroll/button focus).
      if (e.code === "Space") e.preventDefault();
      this.pressed.add(e.code);
    });
    window.addEventListener("keyup", (e) => this.pressed.delete(e.code));
    window.addEventListener("blur", () => this.pressed.clear());

    canvas.addEventListener("mousedown", (e) => {
      this.dragging = true;
      e.preventDefault();
    });
    window.addEventListener("mouseup", () => {
      this.dragging = false;
    });
    window.addEventListener("mousemove", (e) => {
      if (!this.dragging) return;
      this.orbit.yaw = wrapAngle(this.orbit.yaw - e.movementX * LOOK_SENSITIVITY);
      this.orbit.pitch = clamp(
        this.orbit.pitch + e.movementY * LOOK_SENSITIVITY,
        this.flightCamera ? PITCH_MIN_FLIGHT : PITCH_MIN,
        PITCH_MAX,
      );
    });
    canvas.addEventListener("contextmenu", (e) => e.preventDefault());
    canvas.addEventListener(
      "wheel",
      (e) => {
        this.orbit.distance = clamp(
          this.orbit.distance + e.deltaY * 0.01,
          DISTANCE_MIN,
          DISTANCE_MAX,
        );
        e.preventDefault();
      },
      { passive: false },
    );
  }

  /** Current movement intent in world space, camera-relative. */
  moveIntent(): MoveIntent {
    const forward =
      (this.has("KeyW") || this.has("ArrowUp") ? 1 : 0) -
      (this.has("KeyS") || this.has("ArrowDown") ? 1 : 0);
    const strafe =
      (this.has("KeyD") || this.has("ArrowRight") ? 1 : 0) -
      (this.has("KeyA") || this.has("ArrowLeft") ? 1 : 0);
    const sprint = this.has("ShiftLeft") || this.has("ShiftRight");
    const walk = this.walkToggled;
    const climb = (this.has("Space") ? 1 : 0) - (this.has("KeyC") ? 1 : 0);

    if (forward === 0 && strafe === 0) {
      return {
        moveX: 0,
        moveZ: 0,
        yaw: this.lastYaw,
        moving: false,
        sprint,
        walk,
        climb,
        boost: sprint,
      };
    }

    // Camera sits at angle `orbit.yaw` around the avatar; its floor-plane
    // view direction is (-sin yaw, -cos yaw). W walks away from the camera.
    const sin = Math.sin(this.orbit.yaw);
    const cos = Math.cos(this.orbit.yaw);
    let moveX = -sin * forward + cos * strafe;
    let moveZ = -cos * forward - sin * strafe;
    const mag = Math.hypot(moveX, moveZ);
    if (mag > 1) {
      moveX /= mag;
      moveZ /= mag;
    }

    this.lastYaw = Math.atan2(moveX, moveZ);
    return { moveX, moveZ, yaw: this.lastYaw, moving: true, sprint, walk, climb, boost: sprint };
  }

  private has(code: string): boolean {
    return !this.uiFocused && this.pressed.has(code);
  }
}

function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}
